import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Trophy } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center">
              <Trophy className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div className="text-2xl font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              GameLink<span className="text-blue-600">OS</span>
            </div>
          </div>
          <div className="text-sm text-slate-500 uppercase tracking-widest">Internal Operations</div>
        </div>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-8">
            {!token ? (
              <div className="text-center space-y-3">
                <h1 className="text-xl font-bold text-slate-900">Invalid link</h1>
                <p className="text-sm text-slate-500">This reset link is missing a token. Request a new one from the sign-in page.</p>
                <Link to="/forgot-password" className="inline-block text-sm text-blue-600 hover:underline">Request a reset link</Link>
              </div>
            ) : (
              <>
                <h1 className="text-xl font-bold text-slate-900 mb-1">Set a new password</h1>
                <p className="text-sm text-slate-500 mb-6">Use at least 8 characters. Choose something you haven't used before.</p>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label htmlFor="pw" className="text-slate-700">New password</Label>
                    <Input
                      id="pw" type="password" required minLength={8}
                      data-testid="reset-password-input"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cpw" className="text-slate-700">Confirm new password</Label>
                    <Input
                      id="cpw" type="password" required minLength={8}
                      data-testid="reset-confirm-input"
                      value={confirm} onChange={(e) => setConfirm(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="submit" disabled={busy}
                    data-testid="reset-submit-button"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {busy ? "Updating..." : "Update password"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
